/**
 * custom-roast-ui.js
 * CCC custom feature — drives the product-page roast status card
 * and the cart page roast banner.
 *
 * Listens to Horizon's native events:
 *   - 'variant:update'  → update product-page status card
 *   - 'cart:update'     → update cart-page roast banner
 *
 * DOM markers expected on the page
 * ─────────────────────────────────
 * Product page (block/custom-roast-status.liquid):
 *   [data-cru-upcoming]        — upcoming-roast status card (hidden initially)
 *   [data-cru-in-stock]        — in-stock status card (hidden initially)
 *   [data-cru-roast-date]      — text node that receives the formatted roast date
 *   [data-cru-variants]        — <script type="application/json"> containing variant data
 *
 * Cart page (section/main-cart.liquid injects snippets/custom-roast-cart-banner.liquid):
 *   [data-cru-cart-banner]     — banner wrapper (hidden initially)
 *   [data-cru-cart-roast-date] — text node inside banner for roast date
 *   [data-cru-cart-ship-range] — text node inside banner for shipping range
 */

import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { cachedRoastDate, getShippingRange } from '@theme/custom-roast-manager';
import { getUnifiedCartMessage } from '@theme/custom-dispatch-manager';

// ─── Product Page ────────────────────────────────────────────────────────────

let _variantMap = null; // Map<number, { id, title }>

// Stale-result guard: tracks the most recently requested variant ID.
// If another variant:update fires while an async cachedRoastDate() call is
// in-flight, the earlier call discards its result when it resolves.
let _pendingVariantId = null;

// Debounce timer — Horizon fires variant:update once per option group on page
// load (e.g. 2 options = 2 events). Debouncing collapses the burst into one.
let _debounceTimer = null;
const DEBOUNCE_MS = 40;

function loadVariantMap() {
  if (_variantMap) return;
  const el = document.querySelector('[data-cru-variants]');
  if (!el) return;
  try {
    const arr = JSON.parse(el.textContent);
    _variantMap = new Map(arr.map((v) => [v.id, v]));
  } catch (e) {
    console.warn('[CCC] custom-roast-ui: failed to parse variant data', e);
  }
}

async function applyProductPageStatus(variantId) {
  loadVariantMap();
  if (!_variantMap) return;

  const variant = _variantMap.get(variantId);
  if (!variant) return;

  const upcomingEl = document.querySelector('[data-cru-upcoming]');
  const inStockEl = document.querySelector('[data-cru-in-stock]');
  if (!upcomingEl || !inStockEl) return;

  const isUpcoming = variant.title && variant.title.toLowerCase().includes('upcoming roast');

  // Apply visibility synchronously first — this is the definitive state.
  // Do not await before writing hidden flags; stale async results below are
  // guarded by _pendingVariantId so they cannot overwrite this decision.
  if (isUpcoming) {
    inStockEl.hidden = true;
    upcomingEl.hidden = false;
  } else if (variant.available) {
    upcomingEl.hidden = true;
    inStockEl.hidden = false;
  } else {
    upcomingEl.hidden = true;
    inStockEl.hidden = true;
  }

  if (!isUpcoming) return;

  // Fill in the roast date — async, but guarded against stale results.
  _pendingVariantId = variantId;
  const info = await cachedRoastDate();

  // Discard if a newer variant:update has already taken over.
  if (_pendingVariantId !== variantId) return;

  let dateEl = upcomingEl.querySelector('[data-cru-roast-date]');
  const subEl = upcomingEl.querySelector('[data-cru-roast-sub]');
  if (!dateEl) return;
  // Old Liquid markup has <div>Roasting on <span data-cru-roast-date>…</span></div>
  // Target the parent div so the 'Roasting on ' prefix gets replaced too.
  if (dateEl.tagName === 'SPAN') dateEl = dateEl.parentElement;

  if (info) {
    if (info.isToday && info.beforeCutoff) {
      dateEl.textContent = 'Roasting today \u2014 order by 7:00am';
      if (subEl) subEl.textContent = 'Your entire order will ship together once your coffee is roasted.';
    } else if (info.roastDayPastCutoff) {
      dateEl.textContent = `You have missed todays roasting cutoff (7:00am) so if you order now it will be roasted on ${info.formatted}`;
      if (subEl) subEl.textContent = 'Check in after 5:00pm today for any leftover coffee from todays roast if you need it shipped asap.';
    } else if (info.isTomorrow) {
      dateEl.textContent = `Roasting tomorrow, ${info.formatted}`;
      if (subEl) subEl.textContent = 'Your entire order will ship together once your coffee is roasted.';
    } else {
      dateEl.textContent = `Roasting on ${info.formatted}`;
      if (subEl) subEl.textContent = 'Your entire order will ship together once your coffee is roasted.';
    }
  } else {
    dateEl.textContent = 'Roasting on the next roast day';
    if (subEl) subEl.textContent = 'Your entire order will ship together once your coffee is roasted.';
  }
}

// ─── Cart Page ───────────────────────────────────────────────────────────────

/**
 * Live cart delay state from the morphing cart-products island.
 * Prefer this over the banner's initial Liquid attrs — those can stay stale
 * (e.g. banner stayed hidden after qty was raised into partial backorder).
 * @returns {{ hasCoffee: boolean, hasPreorders: boolean, preorderItems: Array<object> } | null}
 */
function readLiveCartDelayState() {
  const el = document.querySelector('script[data-ccc-cart-state]');
  if (!el) return null;
  try {
    const state = JSON.parse(el.textContent || '{}');
    return {
      hasCoffee: Boolean(state.hasCoffee),
      hasPreorders: Boolean(state.hasPreorders),
      preorderItems: Array.isArray(state.preorderItems) ? state.preorderItems : [],
    };
  } catch (e) {
    console.warn('[CCC] Failed to parse cart delay state', e);
    return null;
  }
}

async function applyCartBanner() {
  const bannerEl = document.querySelector('[data-cru-cart-banner]');
  if (!bannerEl) return;

  const live = readLiveCartDelayState();

  let hasCoffee = bannerEl.getAttribute('data-has-coffee') === 'true';
  let hasPreorders = bannerEl.getAttribute('data-has-preorders') === 'true';

  /** @type {Array<{type?: string, vendor?: string, estimatedRestockDate?: string|null, kind?: string}>} */
  let preorderItems = [];

  if (live) {
    hasCoffee = live.hasCoffee;
    hasPreorders = live.hasPreorders;
    preorderItems = live.preorderItems;
    bannerEl.setAttribute('data-has-coffee', hasCoffee ? 'true' : 'false');
    bannerEl.setAttribute('data-has-preorders', hasPreorders ? 'true' : 'false');
    bannerEl.toggleAttribute('hidden', !(hasCoffee || hasPreorders));

    const preorderJsonEl = bannerEl.querySelector('[data-cru-preorder-items]');
    if (preorderJsonEl) {
      preorderJsonEl.textContent = JSON.stringify(preorderItems);
    }
  } else {
    const preorderJsonEl = bannerEl.querySelector('[data-cru-preorder-items]');
    if (preorderJsonEl) {
      try {
        preorderItems = JSON.parse(preorderJsonEl.textContent || '[]');
      } catch (e) {
        console.warn('[CCC] Failed to parse cart banner preorder items', e);
        preorderItems = [];
      }
    }

    // Legacy fallback: pipe-delimited product types only
    if (!preorderItems.length) {
      const preorderTypesStr = bannerEl.getAttribute('data-preorder-types') || '';
      preorderItems = preorderTypesStr
        ? preorderTypesStr.split('|').map((type) => ({ type, vendor: '', estimatedRestockDate: null }))
        : [];
    }

    // Do not early-return on hidden when we have no live island — still allow
    // initial paint when Liquid already showed the banner.
    if (bannerEl.hasAttribute('hidden')) return;
  }

  if (!(hasCoffee || hasPreorders)) return;

  const iconsEl = bannerEl.querySelector('[data-cru-cart-icons]');
  const dateEl = bannerEl.querySelector('[data-cru-cart-ready-date]');
  const explanationEl = bannerEl.querySelector('[data-cru-cart-explanation]');

  if (!getUnifiedCartMessage) {
    if (dateEl) dateEl.textContent = 'calculated shortly';
    return;
  }

  let roastDateObj = null;
  if (hasCoffee) {
    const info = await cachedRoastDate();
    if (info) {
      roastDateObj = new Date(info.roastDate);
    }
  }

  const unifiedMsg = getUnifiedCartMessage(
    hasCoffee,
    hasPreorders,
    preorderItems,
    roastDateObj
  );

  if (iconsEl && unifiedMsg.contextIcons) iconsEl.textContent = unifiedMsg.contextIcons;
  if (dateEl && unifiedMsg.readyMessage) dateEl.textContent = unifiedMsg.readyMessage;
  if (explanationEl && unifiedMsg.explanation) explanationEl.textContent = unifiedMsg.explanation;
}

// ─── Event Listeners ─────────────────────────────────────────────────────────

document.addEventListener(ThemeEvents.variantSelected, (e) => {
  if (e.target instanceof HTMLElement && e.target.closest('product-card, quick-add-dialog')) return;

  const id = e.detail?.resource?.variantId;
  if (!id) return;

  clearTimeout(_debounceTimer);
  applyProductPageStatus(Number(id));
});

document.addEventListener(ThemeEvents.variantUpdate, (e) => {
  const id = e.detail?.resource?.id;
  if (!id) return;
  // Debounce: Horizon fires one event per option group on page load.
  // Collapsing the burst prevents multiple concurrent async calls.
  clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(() => applyProductPageStatus(id), DEBOUNCE_MS);
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(() => {
      requestAnimationFrame(() => applyCartBanner());
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[CCC] custom-roast-ui cart update failed:', error);
    });
});

// ─── Initial Load ─────────────────────────────────────────────────────────────

// Product page — the Liquid block already renders the correct initial
// visibility for the selected variant (hidden/visible flags are set
// server-side). We only need to populate the roast date text node if the
// initially selected variant is 'Upcoming Roast', without toggling any
// visibility (to avoid a flash).
(function initProductPageDateText() {
  loadVariantMap();
  if (!_variantMap) return;

  const urlVariant = new URLSearchParams(window.location.search).get('variant');
  const hiddenInput = document.querySelector('input[name="id"]');
  const initialId = urlVariant
    ? parseInt(urlVariant, 10)
    : hiddenInput
      ? parseInt(hiddenInput.value, 10)
      : null;

  if (!initialId) return;

  const variant = _variantMap.get(initialId);
  if (!variant) return;

  // Only fill the date text — do not toggle visibility (Liquid already correct).
  const isUpcoming = variant.title && variant.title.toLowerCase().includes('upcoming roast');
  if (!isUpcoming) return;

  const upcomingEl = document.querySelector('[data-cru-upcoming]');
  if (!upcomingEl) return;

  _pendingVariantId = initialId;
  cachedRoastDate().then((info) => {
    if (_pendingVariantId !== initialId) return;
    let dateEl = upcomingEl.querySelector('[data-cru-roast-date]');
    const subEl = upcomingEl.querySelector('[data-cru-roast-sub]');
    if (!dateEl) return;
    // Old Liquid markup: <div>Roasting on <span data-cru-roast-date>…</span></div>
    if (dateEl.tagName === 'SPAN') dateEl = dateEl.parentElement;
    if (info) {
      if (info.isToday && info.beforeCutoff) {
        dateEl.textContent = 'Roasting today \u2014 order by 7:00am';
        if (subEl) subEl.textContent = 'Your entire order will ship together once your coffee is roasted.';
      } else if (info.roastDayPastCutoff) {
        dateEl.textContent = `You have missed todays roasting cutoff (7:00am) so if you order now it will be roasted on ${info.formatted}`;
        if (subEl) subEl.textContent = 'Check in after 5:00pm today for any leftover coffee from todays roast if you need it shipped asap.';
      } else if (info.isTomorrow) {
        dateEl.textContent = `Roasting tomorrow, ${info.formatted}`;
        if (subEl) subEl.textContent = 'Your entire order will ship together once your coffee is roasted.';
      } else {
        dateEl.textContent = `Roasting on ${info.formatted}`;
        if (subEl) subEl.textContent = 'Your entire order will ship together once your coffee is roasted.';
      }
    } else {
      dateEl.textContent = 'Roasting on the next roast day';
      if (subEl) subEl.textContent = 'Your entire order will ship together once your coffee is roasted.';
    }
  });
})();

// Cart page — Liquid renders the banner visibility (hidden/shown) based on
// cart contents at render time. But dates are fetched from the Google Calendar
// API, so we need to populate them via JS on initial load.
(function initCartBannerDates() {
  applyCartBanner(null);
})();
